import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';

import Modal from 'common/components/Modal';
import Icon from 'common/components/Icon';
import Typography from 'common/components/Typography';
import Pagination from 'common/components/Pagination';
import LoadingPage from 'common/components/LoadingPage';
import colors from 'common/styles/colors';

import styles from './index.module.css';

const ITEMS_PER_PAGE = 10;

const RewardDetailsModal = ({
  isOpen,
  onClose,
  rewardItem,
  settlementData = null,
  isLoading = false,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  const data = useMemo(() => {
    if (settlementData && settlementData.length > 0) {
      return settlementData;
    }
    return [];
  }, [settlementData]);

  const totalPages = Math.ceil(data.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentData = data.slice(startIndex, endIndex);

  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
    }
  }, [isOpen]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (!rewardItem) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnClickOutside
      closeOnEscape
      modalClassName={styles.modal}
    >
      <div className={styles.content}>
        {isLoading ? (
          <LoadingPage />
        ) : (
          <>
            <div className={styles.header}>
              <div className={styles.headerTop}>
                <Typography
                  tag="h2"
                  size="lg"
                  weight="semiBold"
                  color="color-neutral-900"
                >
                  {rewardItem.period}
                </Typography>
                <button
                  type="button"
                  className={styles.closeButton}
                  onClick={onClose}
                  aria-label="Close modal"
                >
                  <Icon.CrossV2
                    size="medium"
                    fill={colors['rebranding-md-color-grey-50']}
                    description="Close"
                  />
                </button>
              </div>
              <div className={styles.headerBottom}>
                <Typography
                  size="sm"
                  color="color-neutral-400"
                  className={styles.description}
                >
                  Rewards are credited upon settlement and accumulated into a
                  monthly batch, locked for a 3-month vesting period starting on
                  the first day of the month.
                </Typography>
                <div className={styles.infoBoxes}>
                  <div className={styles.infoBox}>
                    <div className={styles.infoBoxHeader}>
                      <Icon.Lock2
                        className={styles.lockIcon}
                        size="xSmall"
                        fill={colors['color-neutral-900']}
                        description="Lockup start"
                      />
                      <Typography
                        size="xs"
                        weight="semiBold"
                        color="color-neutral-900"
                      >
                        Lockup start
                      </Typography>
                    </div>
                    <Typography size="sm" color="color-neutral-900">
                      {rewardItem.startDate}
                    </Typography>
                  </div>
                  <div className={styles.infoBox}>
                    <div className={styles.infoBoxHeader}>
                      <Icon.Lock2OpenedDuotone
                        className={styles.unlockIcon}
                        size="xSmall"
                        fill={colors['color-green-500']}
                        description="Unlock date"
                      />
                      <Typography
                        size="xs"
                        weight="semiBold"
                        color="color-neutral-900"
                      >
                        Unlock date
                      </Typography>
                    </div>
                    <Typography size="sm" color="color-neutral-900">
                      {rewardItem.unlockDate}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.tableSection}>
              <div className={`${styles.table} ${styles.tableDesktop}`}>
                <div className={styles.tableColumn}>
                  <div className={styles.tableHeader}>
                    <Typography
                      size="sm"
                      weight="semiBold"
                      color="color-neutral-900"
                    >
                      Settlement date
                    </Typography>
                  </div>
                  {currentData.map((item, index) => (
                    <div
                      key={`settlement-date-${startIndex + index}`}
                      className={styles.tableCell}
                    >
                      <Typography size="sm" color="color-neutral-900">
                        {item.settlementDate}
                      </Typography>
                    </div>
                  ))}
                </div>
                <div className={styles.tableColumn}>
                  <div className={styles.tableHeader}>
                    <Typography
                      size="sm"
                      weight="semiBold"
                      color="color-neutral-900"
                      className={styles.textRight}
                    >
                      Rate
                    </Typography>
                  </div>
                  {currentData.map((item, index) => (
                    <div
                      key={`rate-${startIndex + index}`}
                      className={styles.tableCell}
                    >
                      <Typography
                        size="sm"
                        color="color-neutral-900"
                        className={styles.textRight}
                      >
                        {item.rate}
                      </Typography>
                    </div>
                  ))}
                </div>
                <div className={styles.tableColumn}>
                  <div className={styles.tableHeader}>
                    <Typography
                      size="sm"
                      weight="semiBold"
                      color="color-neutral-900"
                      className={styles.textRight}
                    >
                      Earned (EUR)
                    </Typography>
                  </div>
                  {currentData.map((item, index) => (
                    <div
                      key={`earned-eur-${startIndex + index}`}
                      className={styles.tableCell}
                    >
                      <Typography
                        size="sm"
                        color="color-neutral-900"
                        className={styles.textRight}
                      >
                        {item.earnedEur}
                      </Typography>
                    </div>
                  ))}
                </div>
                <div className={styles.tableColumn}>
                  <div className={styles.tableHeader}>
                    <Typography
                      size="sm"
                      weight="semiBold"
                      color="color-neutral-900"
                      className={styles.textRight}
                    >
                      Earned (XMN)
                    </Typography>
                  </div>
                  {currentData.map((item, index) => (
                    <div
                      key={`earned-xmn-${startIndex + index}`}
                      className={styles.tableCell}
                    >
                      <Typography
                        size="sm"
                        color="color-neutral-900"
                        className={styles.textRight}
                      >
                        {item.earnedXmn}
                      </Typography>
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.tableMobile}>
                {currentData.map((item, index) => (
                  <div
                    key={`mobile-row-${startIndex + index}`}
                    className={styles.mobileRow}
                  >
                    <div className={styles.mobileColumn}>
                      <Typography size="sm" color="color-neutral-900">
                        {item.earnedXmn}
                      </Typography>
                      <Typography size="xs" color="color-neutral-500">
                        {item.earnedEur}
                      </Typography>
                    </div>
                    <div
                      className={`${styles.mobileColumn} ${styles.mobileColumnRight}`}
                    >
                      <Typography size="sm" color="color-neutral-900">
                        {item.settlementDate}
                      </Typography>
                      <Typography size="xs" color="color-neutral-500">
                        {item.rate} rate
                      </Typography>
                    </div>
                  </div>
                ))}
              </div>

              <div className={styles.pagination}>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  setPage={handlePageChange}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

RewardDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  rewardItem: PropTypes.shape({
    period: PropTypes.string.isRequired,
    startDate: PropTypes.string.isRequired,
    unlockDate: PropTypes.string.isRequired,
  }),
  settlementData: PropTypes.arrayOf(
    PropTypes.shape({
      settlementDate: PropTypes.string.isRequired,
      settlementFee: PropTypes.string.isRequired,
      rate: PropTypes.string.isRequired,
      earnedEur: PropTypes.string.isRequired,
      earnedXmn: PropTypes.string.isRequired,
    })
  ),
  isLoading: PropTypes.bool,
};

RewardDetailsModal.defaultProps = {
  rewardItem: null,
  settlementData: null,
  isLoading: false,
};

export default RewardDetailsModal;
